import { useState, useEffect } from 'react';
import { Plus, Store, Settings2, Edit, Trash2, PlusCircle, XCircle } from 'lucide-react';
import { useIntegrations } from '../contexts/IntegrationsContext';
import { Integration, IntegrationType, OrderStatusMapping } from '../types/integrations';
import { useAuth } from '../contexts/AuthContext';
import { getWooCommerceOrderStatuses } from '../services/woocommerce';
import { shopifyService } from '../services/shopifyService';
import { toast } from '../hooks/use-toast';

const LOGNOW_STATUSES = [
  'pending',
  'preparation',
  'awaitingStock',
  'awaitingPickup',
  'shipped',
  'delivered',
  'cancelled',
  'returned',
  'awaiting_payment'
] as const;

const LOGNOW_STATUS_LABELS: Record<typeof LOGNOW_STATUSES[number], string> = {
  pending: 'Pendente',
  preparation: 'Em preparação',
  awaitingStock: 'Aguardar Stock',
  awaitingPickup: 'Aguardar recolha',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
  returned: 'Devolvido',
  awaiting_payment: 'Aguardando Pagamento'
};

interface WooCommerceStatus {
  id: string;
  name: string;
}

export default function IntegrationsSettings() {
  const { integrations, addIntegration, updateIntegration, deleteIntegration, toggleIntegration, loading, error } = useIntegrations();
  const { currentUser } = useAuth();
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [storeStatuses, setStoreStatuses] = useState<WooCommerceStatus[]>([]);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [newIntegration, setNewIntegration] = useState({
    type: 'woocommerce' as IntegrationType,
    name: '',
    config: {
      url: '',
      consumerKey: '',
      consumerSecret: '',
      syncProducts: true,
      syncOrders: true,
      orderStatusMappings: [] as OrderStatusMapping[]
    }
  });

  const resetNewIntegration = () => {
    setNewIntegration({
      type: 'woocommerce',
      name: '',
      config: {
        url: '',
        consumerKey: '',
        consumerSecret: '',
        syncProducts: true,
        syncOrders: true,
        orderStatusMappings: []
      }
    });
    setStoreStatuses([]);
    setStatusError(null);
  };

  const handleIntegrationTypeChange = (type: IntegrationType) => {
    setNewIntegration(prev => ({
      ...prev,
      type,
      config: type === 'shopify' 
        ? { shopDomain: '', syncProducts: true, syncOrders: true, orderStatusMappings: [] }
        : { url: '', consumerKey: '', consumerSecret: '', syncProducts: true, syncOrders: true, orderStatusMappings: [] }
    }));
  };

  const handleConnectShopify = async () => {
    if (!currentUser?.companyId) return;
    
    const shopDomain = (newIntegration.config as any).shopDomain;
    if (!shopDomain) {
      toast.error('Por favor, insira o domínio da loja Shopify');
      return;
    }

    try {
      // Não adicionar .myshopify.com aqui pois já é adicionado no shopifyService
      const authUrl = await shopifyService.generateAuthUrl(currentUser.companyId, shopDomain);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Erro ao gerar URL de autenticação:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Erro ao conectar com o Shopify');
      }
    }
  };

  useEffect(() => {
    const { url, consumerKey, consumerSecret } = newIntegration.config;
    if (url && consumerKey && consumerSecret) {
      loadWooCommerceStatuses();
    }
  }, [newIntegration.config.url, newIntegration.config.consumerKey, newIntegration.config.consumerSecret]);

  const loadWooCommerceStatuses = async () => {
    const { url, consumerKey, consumerSecret } = newIntegration.config;
    if (!url || !consumerKey || !consumerSecret) return;

    setLoadingStatuses(true);
    setStatusError(null);
    try {
      const statuses = await getWooCommerceOrderStatuses({
        url,
        consumerKey,
        consumerSecret
      });
      setStoreStatuses(statuses);
    } catch (err) {
      console.error('Erro ao carregar estados:', err);
      setStatusError(err instanceof Error ? err.message : 'Erro ao carregar estados da loja');
    } finally {
      setLoadingStatuses(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.companyId) {
      console.error('Company ID not found');
      return;
    }
    
    try {
      if (isEditing) {
        await updateIntegration(isEditing, {
          ...newIntegration,
          updatedAt: new Date().toISOString()
        });
        setIsEditing(null);
      } else {
        await addIntegration({
          ...newIntegration,
          active: true,
          companyId: currentUser.companyId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      setIsAddingNew(false);
      resetNewIntegration();
    } catch (err) {
      console.error('Erro ao salvar integração:', err);
    }
  };

  const handleEdit = (integration: Integration) => {
    const config = {
      ...integration.config,
      orderStatusMappings: integration.config.orderStatusMappings || []
    };

    setNewIntegration({
      type: integration.type,
      name: integration.name,
      config: config as any
    });
    
    // Se for Shopify, carregar os status padrão do Shopify
    if (integration.type === 'shopify') {
      setStoreStatuses([
        { id: 'pending', name: 'Pendente' },
        { id: 'processing', name: 'Em processamento' },
        { id: 'completed', name: 'Concluído' },
        { id: 'cancelled', name: 'Cancelado' },
        { id: 'refunded', name: 'Reembolsado' },
        { id: 'failed', name: 'Falhou' },
        { id: 'on-hold', name: 'Em espera' }
      ]);
    }
    
    setIsEditing(integration.id);
    setIsAddingNew(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta integração?')) {
      try {
        await deleteIntegration(id);
      } catch (err) {
        console.error('Erro ao excluir integração:', err);
      }
    }
  };

  const handleAddStatusMapping = () => {
    setNewIntegration({
      ...newIntegration,
      config: {
        ...newIntegration.config,
        orderStatusMappings: [
          ...newIntegration.config.orderStatusMappings,
          { lognow: LOGNOW_STATUSES[0], store: storeStatuses[0].id }
        ]
      }
    });
  };

  const handleRemoveStatusMapping = (index: number) => {
    setNewIntegration({
      ...newIntegration,
      config: {
        ...newIntegration.config,
        orderStatusMappings: newIntegration.config.orderStatusMappings.filter((_, i) => i !== index)
      }
    });
  };

  const handleUpdateStatusMapping = (index: number, field: 'lognow' | 'store', value: string) => {
    const newMappings = [...newIntegration.config.orderStatusMappings];
    newMappings[index] = {
      ...newMappings[index],
      [field]: value
    };
    setNewIntegration({
      ...newIntegration,
      config: {
        ...newIntegration.config,
        orderStatusMappings: newMappings
      }
    });
  };

  if (loading) {
    return <div className="p-4">A carregar...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Integrações</h1>
        {!isAddingNew && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            Nova Integração
          </button>
        )}
      </div>

      {isAddingNew ? (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">
            {isEditing ? 'Editar Integração' : 'Nova Integração'}
          </h2>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Tipo de Integração
              </label>
              <select
                value={newIntegration.type}
                onChange={(e) => handleIntegrationTypeChange(e.target.value as IntegrationType)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
              >
                <option value="woocommerce">WooCommerce</option>
                <option value="shopify">Shopify</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Nome da Integração
              </label>
              <input
                type="text"
                value={newIntegration.name}
                onChange={(e) => setNewIntegration({ ...newIntegration, name: e.target.value })}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Ex: Minha Loja"
              />
            </div>

            {newIntegration.type === 'woocommerce' ? (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    URL da Loja
                  </label>
                  <input
                    type="text"
                    value={(newIntegration.config as any).url}
                    onChange={(e) => setNewIntegration({
                      ...newIntegration,
                      config: { ...newIntegration.config, url: e.target.value }
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="https://minhaloja.com"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Consumer Key
                  </label>
                  <input
                    type="text"
                    value={(newIntegration.config as any).consumerKey}
                    onChange={(e) => setNewIntegration({
                      ...newIntegration,
                      config: { ...newIntegration.config, consumerKey: e.target.value }
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Consumer Secret
                  </label>
                  <input
                    type="password"
                    value={(newIntegration.config as any).consumerSecret}
                    onChange={(e) => setNewIntegration({
                      ...newIntegration,
                      config: { ...newIntegration.config, consumerSecret: e.target.value }
                    })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>

                {storeStatuses.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Mapeamento de Estados dos Pedidos</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Configure como os estados dos pedidos no LOGNOW correspondem aos estados na sua loja online.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddStatusMapping}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Adicionar Mapeamento
                      </button>
                    </div>

                    {newIntegration.config.orderStatusMappings.map((mapping, index) => (
                      <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado no LOGNOW
                          </label>
                          <select
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                            value={mapping.lognow}
                            onChange={(e) => handleUpdateStatusMapping(index, 'lognow', e.target.value)}
                          >
                            {LOGNOW_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {LOGNOW_STATUS_LABELS[status]}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex flex-col items-center justify-center px-4">
                          <span className="text-gray-500">corresponde a</span>
                          <span className="text-gray-500">↓</span>
                        </div>

                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado na Loja WooCommerce
                          </label>
                          <select
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                            value={mapping.store}
                            onChange={(e) => handleUpdateStatusMapping(index, 'store', e.target.value)}
                          >
                            {storeStatuses.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveStatusMapping(index)}
                          className="text-red-500 hover:text-red-600 p-2"
                          title="Remover mapeamento"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    ))}

                    {loadingStatuses ? (
                      <div className="text-center py-8">
                        <p className="text-gray-600">Carregando estados da loja...</p>
                      </div>
                    ) : statusError ? (
                      <div className="text-center py-8">
                        <p className="text-red-600">{statusError}</p>
                        <button
                          type="button"
                          onClick={loadWooCommerceStatuses}
                          className="mt-2 text-primary hover:text-primary-dark"
                        >
                          Tentar novamente
                        </button>
                      </div>
                    ) : storeStatuses.length === 0 && (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                        <p className="text-gray-600">
                          Preencha as credenciais da loja WooCommerce para carregar os estados disponíveis.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Domínio da Loja Shopify
                  </label>
                  <input
                    type="text"
                    value={(newIntegration.config as any).shopDomain}
                    onChange={(e) => setNewIntegration({
                      ...newIntegration,
                      config: { ...newIntegration.config, shopDomain: e.target.value }
                    })}
                    placeholder="minhaloja"
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    disabled={isEditing}
                  />
                  <p className="text-sm text-gray-500">Apenas o nome da loja, sem o .myshopify.com</p>
                </div>

                <div className="space-y-2 mt-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={(newIntegration.config as any).syncProducts}
                      onChange={(e) => setNewIntegration({
                        ...newIntegration,
                        config: { ...newIntegration.config, syncProducts: e.target.checked }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Sincronização de Produtos</span>
                  </label>
                </div>

                <div className="space-y-2 mt-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={(newIntegration.config as any).syncOrders}
                      onChange={(e) => setNewIntegration({
                        ...newIntegration,
                        config: { ...newIntegration.config, syncOrders: e.target.checked }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Sincronização de Pedidos</span>
                  </label>
                </div>

                {isEditing && storeStatuses.length > 0 && (
                  <div className="space-y-4 mt-6">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">Mapeamento de Estados dos Pedidos</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Configure como os estados dos pedidos no LOGNOW correspondem aos estados no Shopify.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddStatusMapping}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        <PlusCircle className="h-4 w-4 mr-1" />
                        Adicionar Mapeamento
                      </button>
                    </div>

                    {newIntegration.config.orderStatusMappings.map((mapping, index) => (
                      <div key={index} className="flex items-center gap-4 bg-gray-50 p-4 rounded-lg">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado no LOGNOW
                          </label>
                          <select
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                            value={mapping.lognow}
                            onChange={(e) => handleUpdateStatusMapping(index, 'lognow', e.target.value)}
                          >
                            {LOGNOW_STATUSES.map((status) => (
                              <option key={status} value={status}>
                                {LOGNOW_STATUS_LABELS[status]}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex flex-col items-center justify-center px-4">
                          <span className="text-gray-500">corresponde a</span>
                          <span className="text-gray-500">↓</span>
                        </div>

                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Estado no Shopify
                          </label>
                          <select
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                            value={mapping.store}
                            onChange={(e) => handleUpdateStatusMapping(index, 'store', e.target.value)}
                          >
                            {storeStatuses.map((status) => (
                              <option key={status.id} value={status.id}>
                                {status.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveStatusMapping(index)}
                          className="text-red-500 hover:text-red-600 p-2"
                          title="Remover mapeamento"
                        >
                          <XCircle className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {!isEditing && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleConnectShopify}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                      <Store className="h-5 w-5 mr-2" />
                      Conectar com Shopify
                    </button>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setIsAddingNew(false);
                  resetNewIntegration();
                  setIsEditing(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancelar
              </button>

              {(newIntegration.type === 'woocommerce' || (newIntegration.type === 'shopify' && isEditing)) && (
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  {isEditing ? 'Guardar Alterações' : 'Adicionar Integração'}
                </button>
              )}
            </div>
          </div>
        </form>
      ) : (
        <div className="grid gap-4">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              className="bg-white p-6 rounded-lg shadow-md"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Store size={24} className="text-gray-600" />
                  <div>
                    <h3 className="font-semibold">{integration.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{integration.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(integration)}
                    className="p-2 text-gray-600 hover:text-primary"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(integration.id)}
                    className="p-2 text-gray-600 hover:text-red-600"
                  >
                    <Trash2 size={20} />
                  </button>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={integration.active}
                      onChange={() => toggleIntegration(integration.id)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                {integration.type === 'woocommerce' ? (
                  <>
                    <p>URL: {(integration.config as any).url}</p>
                    <p>Sincronização de Produtos: {(integration.config as any).syncProducts ? 'Sim' : 'Não'}</p>
                    <p>Sincronização de Pedidos: {(integration.config as any).syncOrders ? 'Sim' : 'Não'}</p>
                  </>
                ) : integration.type === 'shopify' ? (
                  <>
                    <p>Domínio: {(integration.config as any).shopDomain}</p>
                    <p>Sincronização de Produtos: {(integration.config as any).syncProducts ? 'Sim' : 'Não'}</p>
                    <p>Sincronização de Pedidos: {(integration.config as any).syncOrders ? 'Sim' : 'Não'}</p>
                  </>
                ) : null}
              </div>
              <div className="mt-4 border-t pt-4">
                <h4 className="font-semibold mb-2">Mapeamento de Estados dos Pedidos</h4>
                {(integration.config as any).orderStatusMappings?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(integration.config as any).orderStatusMappings.map((mapping: OrderStatusMapping, index: number) => (
                      <div key={index} className="flex items-center gap-2 text-sm bg-gray-50 p-3 rounded-lg">
                        <span className="font-medium text-gray-700">{LOGNOW_STATUS_LABELS[mapping.lognow]}</span>
                        <span className="text-gray-400">→</span>
                        <span className="font-medium text-gray-700">{mapping.store}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">
                    Nenhum mapeamento configurado. Edite a integração para configurar os mapeamentos.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
